/**
 * Figma: https://www.figma.com/design/XwMQV9iBJ1Ec4sOzWMEZcK/-Dev--Customer-Hub-2.3-%E2%80%93-Phase-1?node-id=2226-105974&m=dev
 */

import { useLabel } from '../../../i18n'
import { formatPrice } from '../../../utils/currency'
import type { Order } from '../types'

interface OrderSummaryProps {
  subtotal: Order['subtotal']
  salesTax: Order['salesTax']
  shippingChargeAmount: Order['shippingChargeAmount']
  total: Order['total']
  currency: Order['currency']
  shippingAddress: Order['shippingAddress']
}

const formatCurrency = (amount: number, currency: string): string => {
  if (amount === undefined || amount === null)
    return formatPrice(0, currency, 0)
  const hasDecimals = Math.abs(amount - Math.trunc(amount)) > 1e-6
  return formatPrice(amount, currency, hasDecimals ? 2 : 0)
}

export default function OrderSummary({
  subtotal,
  salesTax,
  shippingChargeAmount,
  total,
  currency,
  shippingAddress,
}: OrderSummaryProps) {
  const t = useLabel()

  return (
    <div className="flex flex-col gap-20">
      {/* Breakdown rows */}
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <span className="text-sm leading-sm lg:text-base lg:leading-base font-medium">
            {t('orderList.summary.subtotal')}
          </span>
          <span className="text-sm leading-sm lg:text-base lg:leading-base font-medium">
            {formatCurrency(subtotal, currency)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm leading-sm font-light text-text">
            {t('orderList.summary.salesTax')}
          </span>
          <span className="text-sm leading-sm font-light text-text">
            {formatCurrency(salesTax, currency)}
          </span>
        </div>
        {shippingAddress && (
          <div className="flex items-center justify-between">
            <span className="text-sm leading-sm font-light text-text">
              {t('orderList.summary.shipping')}
            </span>
            <span className="text-sm leading-sm font-light text-text">
              {shippingChargeAmount > 0
                ? formatCurrency(shippingChargeAmount, currency)
                : t('orderList.summary.free')}
            </span>
          </div>
        )}
      </div>

      {/* Total */}
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-4">
          <span className="text-base leading-base font-medium text-text">
            {t('orderList.summary.total')}
          </span>
          <span className="text-xs leading-xs lg:text-sm lg:leading-sm font-light text-text-secondary">
            {t('orderList.summary.inclVat')}
          </span>
        </div>
        <span className="text-base leading-base font-medium text-text">
          {formatCurrency(total, currency)}
        </span>
      </div>
    </div>
  )
}
